import React, { useState } from 'react';

/**
 * SVG Pickleball court layout with player positions.
 * isAdmin: enables click-to-assign
 * courtPositions: { TL: playerId, BL: playerId, TR: playerId, BR: playerId }
 */
export default function CourtLayout({
  match, team1Players, team2Players, courtPositions = {},
  isAdmin, onPositionChange, serverTeamId, serverPlayerId
}) {
  const [selectingZone, setSelectingZone] = useState(null);

  // Court is horizontal. Net is vertical at x=160.
  // Kitchen/NVZ: x=112–160 (T1 side), x=160–208 (T2 side).
  // Players of same team are vertically aligned (same x-center):
  //   T1 back: cx=55,  cy=65  (zone x=35, y=45)
  //   T1 kit:  cx=55,  cy=175 (zone x=35, y=155)
  //   T2 back: cx=265, cy=65  (zone x=245, y=45)
  //   T2 kit:  cx=265, cy=175 (zone x=245, y=155)
  // Note: T1 kitchen player is drawn at x=55 (back court area) but labeled "Kitchen"
  // to keep vertical alignment. The shaded kitchen band still shows actual NVZ area.
  const zones = [
    { id: 'TL', x: 35,  y: 45,  label: 'T1 Back',    team: 1 },
    { id: 'BL', x: 35,  y: 155, label: 'T1 Kitchen', team: 1 },
    { id: 'TR', x: 245, y: 45,  label: 'T2 Back',    team: 2 },
    { id: 'BR', x: 245, y: 155, label: 'T2 Kitchen', team: 2 },
  ];

  const getPlayerInZone = (zoneId) => {
    const pid = courtPositions[zoneId];
    if (!pid) return null;
    return [...(team1Players || []), ...(team2Players || [])].find(p => p.id === pid);
  };

  const handleZoneClick = (zoneId) => {
    if (!isAdmin) return;
    setSelectingZone(selectingZone === zoneId ? null : zoneId);
  };

  const handleAssign = (zoneId, player) => {
    const newPos = { ...courtPositions };
    Object.keys(newPos).forEach(z => { if (newPos[z] === player?.id) delete newPos[z]; });
    if (player) newPos[zoneId] = player.id;
    else delete newPos[zoneId];
    onPositionChange && onPositionChange(newPos);
    setSelectingZone(null);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px', textAlign: 'center' }}>
        Court View (Top Down) {isAdmin && <span style={{ color: '#27ae60' }}>· Click zone to assign player</span>}
      </div>

      {/* SVG viewBox expanded on sides to fit team name labels */}
      <svg
        viewBox="-55 -5 430 260"
        style={{ width: '100%', maxWidth: '520px', display: 'block', margin: '0 auto' }}
      >
        {/* ── Court background ── */}
        <rect x="0" y="0" width="320" height="240" fill="#0d2137" rx="6" />

        {/* ── Back court areas (blue) ── */}
        {/* T1 back court: x=10 to x=112 */}
        <rect x="10" y="10" width="102" height="220" fill="#1a4a7a" rx="0" />
        {/* T2 back court: x=208 to x=310 */}
        <rect x="208" y="10" width="102" height="220" fill="#1a4a7a" rx="0" />

        {/* ── Kitchen / NVZ areas (red) ── */}
        {/* T1 kitchen: x=112 to x=160 */}
        <rect x="112" y="10" width="48" height="220" fill="#8b1a1a" />
        {/* T2 kitchen: x=160 to x=208 */}
        <rect x="160" y="10" width="48" height="220" fill="#8b1a1a" />

        {/* ── Court outer border ── */}
        <rect x="10" y="10" width="300" height="220" fill="none" stroke="#4a9ede" strokeWidth="2" rx="4" />

        {/* ── Kitchen boundary lines ── */}
        <line x1="112" y1="10" x2="112" y2="230" stroke="#e74c3c" strokeWidth="2" />
        <line x1="208" y1="10" x2="208" y2="230" stroke="#e74c3c" strokeWidth="2" />

        {/* ── Center net ── */}
        <line x1="160" y1="10" x2="160" y2="230" stroke="#c8e6c9" strokeWidth="3" strokeDasharray="6 3" />
        <rect x="155" y="112" width="10" height="16" fill="#2d5a27" rx="2" />
        <text x="160" y="123" textAnchor="middle" fill="#c8e6c9" fontSize="6" fontWeight="600">NET</text>

        {/* ── Service-court center lines (back courts) ── */}
        <line x1="61" y1="10" x2="61" y2="230" stroke="#4a9ede" strokeWidth="1" opacity="0.35" />
        <line x1="259" y1="10" x2="259" y2="230" stroke="#4a9ede" strokeWidth="1" opacity="0.35" />

        {/* ── Horizontal mid-court lines (back courts only) ── */}
        <line x1="10" y1="120" x2="112" y2="120" stroke="#4a9ede" strokeWidth="1" opacity="0.35" />
        <line x1="208" y1="120" x2="310" y2="120" stroke="#4a9ede" strokeWidth="1" opacity="0.35" />

        {/* ── KITCHEN labels ── */}
        <text x="136" y="123" textAnchor="middle" fill="rgba(255,180,180,0.6)" fontSize="6" letterSpacing="0.5">NVZ</text>
        <text x="184" y="123" textAnchor="middle" fill="rgba(255,180,180,0.6)" fontSize="6" letterSpacing="0.5">NVZ</text>

        {/* ── Team name labels OUTSIDE the court ── */}
        <text
          x="-42" y="120"
          textAnchor="middle"
          fill="#4dd18f"
          fontSize="9"
          fontWeight="700"
          transform="rotate(-90, -42, 120)"
          letterSpacing="0.5"
        >
          {(match?.team1_name || 'Team 1').toUpperCase()}
        </text>
        <text
          x="362" y="120"
          textAnchor="middle"
          fill="#5dade2"
          fontSize="9"
          fontWeight="700"
          transform="rotate(90, 362, 120)"
          letterSpacing="0.5"
        >
          {(match?.team2_name || 'Team 2').toUpperCase()}
        </text>

        {/* ── Player zones ── */}
        {zones.map(zone => {
          const player = getPlayerInZone(zone.id);
          const isSelected = selectingZone === zone.id;
          const isCurrentServer = !!(serverPlayerId && player && player.id === serverPlayerId);
          const isServingTeam = serverTeamId && (zone.team === 1 ? match?.team1_id === serverTeamId : match?.team2_id === serverTeamId);
          const isServer = !!(isServingTeam && player);
          const teamColor = zone.team === 1 ? '#27ae60' : '#2980b9';
          const cx = zone.x + 20;
          const cy = zone.y + 20;

          return (
            <g key={zone.id} onClick={() => handleZoneClick(zone.id)}
              style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
              {/* Zone circle */}
              <circle
                cx={cx} cy={cy}
                r={isSelected ? 22 : 18}
                fill={player ? teamColor : 'rgba(255,255,255,0.1)'}
                stroke={isSelected ? '#fff' : player ? teamColor : 'rgba(255,255,255,0.25)'}
                strokeWidth={isSelected ? 2 : 1.5}
                opacity={isSelected ? 1 : 0.9}
              />
              {/* Serving team dashed ring */}
              {isServer && !isCurrentServer && (
                <circle cx={cx} cy={cy} r={24}
                  fill="none" stroke="#FFD700" strokeWidth="1.5" opacity="0.5" strokeDasharray="4 2" />
              )}
              {/* Current server — solid gold ring + label */}
              {isCurrentServer && (
                <>
                  <circle cx={cx} cy={cy} r={25}
                    fill="none" stroke="#FFD700" strokeWidth="3" />
                  <text x={cx} y={zone.y - 2} textAnchor="middle" fill="#FFD700" fontSize="8" fontWeight="800">SERVING</text>
                </>
              )}
              {/* Player initial or + */}
              <text x={cx} y={cy + 4} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">
                {player ? player.name.charAt(0).toUpperCase() : (isAdmin ? '+' : '?')}
              </text>
              {/* Player name label below circle */}
              {player && (
                <text x={cx} y={zone.y + 48} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="7">
                  {player.name.split(' ')[0]}
                </text>
              )}
              {/* Zone label (Back / Kitchen) when empty */}
              {!player && (
                <text x={cx} y={zone.y + 48} textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="6">
                  {zone.label.split(' ')[1]}
                </text>
              )}
            </g>
          );
        })}

        {/* ── Serving indicator bar at bottom ── */}
        {serverTeamId && (() => {
          const sp = serverPlayerId ? [...(team1Players || []), ...(team2Players || [])].find(p => p.id === serverPlayerId) : null;
          const label = sp ? `● SERVING · ${sp.name}` : '● SERVING';
          return serverTeamId === match?.team1_id
            ? <text x="14" y="226" fill="#FFD700" fontSize="7" fontWeight="700">{label}</text>
            : <text x="212" y="226" fill="#FFD700" fontSize="7" fontWeight="700">{label}</text>;
        })()}
      </svg>

      {/* Zone selection popup */}
      {selectingZone && isAdmin && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: '#1a2332', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '10px', padding: '16px', zIndex: 10, minWidth: '200px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '10px', fontWeight: '600' }}>
            Assign to {selectingZone}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <button onClick={() => handleAssign(selectingZone, null)}
              style={{ padding: '8px', borderRadius: '6px', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              Empty
            </button>
            {(selectingZone.endsWith('L') ? team1Players : team2Players).map(p => (
              <button key={p.id} onClick={() => handleAssign(selectingZone, p)}
                style={{
                  padding: '8px', borderRadius: '6px', cursor: 'pointer', border: 'none', textAlign: 'left',
                  background: courtPositions[selectingZone] === p.id ? '#27ae60' : 'rgba(255,255,255,0.08)',
                  color: courtPositions[selectingZone] === p.id ? '#fff' : 'rgba(255,255,255,0.8)',
                  fontWeight: '600', fontSize: '13px',
                }}>
                {p.name}
              </button>
            ))}
          </div>
          <button onClick={() => setSelectingZone(null)}
            style={{ marginTop: '8px', width: '100%', padding: '6px', borderRadius: '6px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '12px' }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
